/**
 * The funnel aggregate the dashboard's funnel view reads.
 */

import { DEMO_CONFIG, PLATFORMS, PLATFORM_MODIFIERS, type PlatformKey } from './demoDataConfig';
import { generateCampaigns, type DemoCampaign } from './demoDataFixtures';
import { classifyCampaignByFunnel } from '@/config/funnelConfig';

// ============================================================================
// FUNNEL DATA - Realistic performance tiers
// ============================================================================

export interface FunnelStageData {
  stage: 'aware' | 'consider' | 'convert'
  label: string
  value: number // Percentage of previous stage
  count: number // Absolute count
  rate: number // Percentage from first stage
  color: string
}

export interface ChannelContributor {
  name: string
  value: number
  percentage: number
}

export interface FunnelData {
  stages: FunnelStageData[]
  channelContributors: Record<string, ChannelContributor[]>
  summary: {
    totalReach: number
    engagementRate: number
    leadRate: number
    overallConversion: number
  }
}

/**
 * Generate realistic funnel data based on campaign performance tiers
 */
export function getDemoFunnelData(): FunnelData {
  const activeCampaigns = generateCampaigns().filter((c) => c.status === 'active')

  // Use the demo configuration's funnel
  const funnel = DEMO_CONFIG.funnel
  const awareStageKey = funnel.stages[0]?.key || 'aware'
  const considerStageKey = funnel.stages[1]?.key || 'consider'
  const convertStageKey = funnel.stages[2]?.key || 'convert'

  // Calculate stage counts based on campaign tiers and objectives
  const awarenessCampaigns = activeCampaigns.filter(
    (c) => c.objective === 'awareness' || classifyCampaignByFunnel(c.name, funnel) === awareStageKey
  )
  const considerationCampaigns = activeCampaigns.filter(
    (c) =>
      c.objective === 'consideration' ||
      classifyCampaignByFunnel(c.name, funnel) === considerStageKey
  )
  const conversionCampaigns = activeCampaigns.filter(
    (c) =>
      c.objective === 'conversion' || classifyCampaignByFunnel(c.name, funnel) === convertStageKey
  )

  // Base reach from campaign budgets and tier performance
  const calculateReach = (campaigns: DemoCampaign[]): number => {
    return campaigns.reduce((sum, c) => {
      const tierMultiplier =
        c.performanceTier === 'tier1'
          ? 1.5
          : c.performanceTier === 'tier2'
            ? 1.2
            : c.performanceTier === 'tier3'
              ? 0.9
              : 0.6
      return sum + (c.budget / 15) * tierMultiplier * 1000
    }, 0)
  }

  const awareCount = Math.floor(
    calculateReach(awarenessCampaigns) +
      calculateReach(considerationCampaigns) +
      calculateReach(conversionCampaigns)
  )
  const considerCount = Math.floor(awareCount * (0.35 + Math.random() * 0.15)) // 35-50% drop-off
  const convertCount = Math.floor(considerCount * (0.25 + Math.random() * 0.15)) // 25-40% drop-off

  // Channel contributions based on platform modifiers and campaign tiers
  const calculateContributors = (stage: 'aware' | 'consider' | 'convert'): ChannelContributor[] => {
    const platforms: PlatformKey[] = [
      'facebook',
      'google_display',
      'google_search',
      'performance_max',
      'tiktok',
      'line',
    ]

    return platforms
      .map((platform) => {
        const platformCampaigns = activeCampaigns.filter((c) => c.platform === platform)
        const totalBudget = platformCampaigns.reduce((sum, c) => sum + c.budget, 0)

        // Calculate contribution based on platform modifier and campaign tier
        let tierScore = 0
        platformCampaigns.forEach((c) => {
          tierScore +=
            c.performanceTier === 'tier1'
              ? 4
              : c.performanceTier === 'tier2'
                ? 3
                : c.performanceTier === 'tier3'
                  ? 2
                  : 1
        })
        const avgTier = platformCampaigns.length > 0 ? tierScore / platformCampaigns.length : 2

        const modifier = PLATFORM_MODIFIERS[platform]
        const baseValue = (totalBudget / 1000) * avgTier * modifier.roasMultiplier

        // Stage-specific adjustments
        const stageMultiplier =
          stage === 'aware'
            ? 1.0
            : stage === 'consider'
              ? modifier.ctrMultiplier
              : modifier.roasMultiplier

        return {
          name: PLATFORMS[platform].name,
          value: Math.floor(baseValue * stageMultiplier),
          percentage: 0, // Will be calculated
        }
      })
      .sort((a, b) => b.value - a.value)
      .map((c, _, arr) => ({
        ...c,
        percentage: (c.value / arr.reduce((s, x) => s + x.value, 0)) * 100,
      }))
  }

  const stages: FunnelStageData[] = [
    {
      stage: 'aware',
      label: funnel.stages[0]?.label || 'Reach',
      value: 100,
      count: awareCount,
      rate: 100,
      color: 'from-primary-500 to-primary-600',
    },
    {
      stage: 'consider',
      label: funnel.stages[1]?.label || 'Engaged',
      value: (considerCount / awareCount) * 100,
      count: considerCount,
      rate: (considerCount / awareCount) * 100,
      color: 'from-purple-500 to-purple-600',
    },
    {
      stage: 'convert',
      label: funnel.stages[2]?.label || 'Leads',
      value: (convertCount / considerCount) * 100,
      count: convertCount,
      rate: (convertCount / awareCount) * 100,
      color: 'from-green-500 to-green-600',
    },
  ]

  return {
    stages,
    channelContributors: {
      aware: calculateContributors('aware'),
      consider: calculateContributors('consider'),
      convert: calculateContributors('convert'),
    },
    summary: {
      totalReach: awareCount,
      engagementRate: stages[1].rate,
      leadRate: stages[2].rate,
      overallConversion: stages[2].rate,
    },
  }
}
